import { unset } from "lodash-es";
import { UPLOADED_FILE_TARGET_SIZE_BY_DIMENSION_FN, GIF_TARGET_SIZE_PER_FILE_FN, GIF_MAX_FILE_SIZE_VERDICT_FN } from "../../utils/options.utils";
import { StringUtils } from "../../utils/string-utils";
import { isHidden, Requirement } from './models/option-requirement';

export enum OptionLevel {
    Simple = "SIMPLE",
    Advanced = "ADVANCED"
}

export class Note {
    text: string;
    textClassName?: string
    textFn?: string;
    hidden?: Requirement[];
    hiddenFn?: string;
    /** xconfig's site-feature-flag marker; evaluated by `pdf-compress-presets.gate.ts`. */
    whenFeatureFlag?: { [flagName: string]: boolean };
    /** What the note IS (xconfig); `warning` gets a non-colour cue. Never inferred from textClassName. */
    severity?: 'warning';
}


export interface RestoreContext {
    id: string;
    condition: Requirement[] | Requirement;
    defaultValues?: Record<string, any>;
    hiddenOptions?: string[];
}

export type ViewResult = { [key: string]: any };


export class ViewState {
    uuid: string = StringUtils.getRandomString("a")
    id: string;
    parent?: ViewState;

    ensureContextState(results: ViewResult) {
        // Default implementation does nothing
    }

    getSnapshot(): any {
        return undefined;
    }

    restoreSnapshot(snapshot: any) {
        // Default implementation does nothing
    }

    hasAdvancedOptions(): boolean {
        return false;
    }

    findByPropertyName(propertyName: string): ViewState | undefined {
        return undefined;
    }

    find(predicate: (id: string) => boolean): ViewState | undefined {
        if (predicate(this.id)) {
            return this;
        }
        return undefined;
    }
}

export abstract class OptionViewState extends ViewState {
    /**
     * This returns the result for the view without any hidden options or restored state.
     *
     * @param context the values already known while assembling the view result
     * (root properties + everything seeded so far). Needed so nested nodes with a
     * shared property and disjoint hidden clauses (e.g. the per-process RANGE
     * variants revealed under a dropdown entry) can tell which sibling owns the
     * value — a definitely-hidden sharer must not seed it (walk order would let
     * it clobber the visible owner, mirroring the group-level rule in
     * RootOptionViewState.getResultForView).
     */
    abstract getResultForView(context?: ViewResult): ViewResult;

    /**
     * 
     * @param results ViewResult passed to remove items that are hidden on the UI
     * @returns 
     */
    abstract summarize(results: ViewResult): ViewResult;
}

class RootProperty {
    public propertyName: string;
    public propertyValue: string | string[];
}

export class RootOptionViewState extends OptionViewState {
    id: any;
    name: string;
    selected: boolean;
    properties: RootProperty[];
    options: GroupViewState[];

    setProperty(propertyName: string, propertyValue: string | string[]) {
        const property = this.properties.find(it => it.propertyName === propertyName);
        if (property) {
            property.propertyValue = propertyValue;
        } else {
            this.properties.push({ propertyName, propertyValue });
        }
    }

    unsetProperty(propertyName: string) {
        this.properties = this.properties.filter(it => it.propertyName !== propertyName);
    }

    getPropertyValue(propertyName: string): string | string[] {
        return this.properties.find(it => it.propertyName === propertyName)?.propertyValue;
    }

    selectChild(model: OptionViewState) {
        throw new Error("Method not implemented.");
    }

    override findByPropertyName(propertyName: string): ViewState | undefined {
        if (this.properties?.some(it => it.propertyName === propertyName)) {
            return this;
        }
        for (const opt of this.options) {
            const found = opt.findByPropertyName(propertyName);
            if (found) return found;
        }
        return undefined;
    }

    findNodeByPropertyName(startNode: ViewState, propertyName: string): ViewState | undefined {
        return startNode.findByPropertyName(propertyName);
    }

    override find(predicate: (id: string) => boolean): ViewState | undefined {
        if (predicate(this.id)) {
            return this;
        }
        for (const opt of this.options) {
            const found = opt.find(predicate);
            if (found) return found;
        }
        return undefined;
    }

    findNode(startNode: ViewState, predicate: (id: string) => boolean): ViewState | undefined {
        return startNode.find(predicate);
    }

    override getResultForView(): ViewResult {
        const result = {};
        this.properties?.forEach(it => {
            result[it.propertyName] = it.propertyValue;
        });
        const groupViewStates = this.options
            .filter(it => it instanceof GroupViewState)
        groupViewStates.forEach(it => {
            // A hidden group that SHARES its property with another group must not
            // seed the view results: walk order would let it clobber the visible
            // owner's value, and every hide-condition keyed on that property would
            // then evaluate against the wrong mode during summarize. The fixed page
            // discriminators (process/toMedia/toExtension) are already in `result`
            // here, so the hidden check is stable for cross-container mode groups.
            const group = it as GroupViewState;
            if (group.propertyIsSharedWithSiblingGroup && group.isHidden(result)) {
                return;
            }
            Object.assign(result, it.getResultForView(result))
        });
        return result;
    }

    override summarize(results: ViewResult): ViewResult {
        const result = {};
        this.properties?.forEach(it => {
            result[it.propertyName] = it.propertyValue;
        });
        const groupViewStates = this.options
            .filter(it => it instanceof GroupViewState)
        groupViewStates.forEach(it => {
            Object.assign(result, it.summarize(results))
        });
        return result;
    }

    // Root doesn't typically need state restoration logic triggered by itself, 
    // but if we wanted to snapshot the whole tree:
    getSnapshot() {
        return this.options.map(o => o.getSnapshot());
    }

    restoreSnapshot(snapshot: any) {
        if (Array.isArray(snapshot)) {
            this.options.forEach((o, i) => o.restoreSnapshot(snapshot[i]));
        }
    }

    restoreDefaultValues(values: Record<string, any>) {
        this.options.forEach(opt => {
            if (typeof opt.restoreDefaultValues === 'function') {
                opt.restoreDefaultValues(values);
            }
        });
    }

    hasAdvancedOptions(): boolean {
        return this.options.some(it => it.hasAdvancedOptions());
    }
}

export class GroupViewState extends OptionViewState {
    id: any;
    name: string;
    propertyNames: string[];
    notes?: Note[];
    hidden: Requirement[];
    optionLevel: OptionLevel = OptionLevel.Simple;
    options: SelectableOptionViewState[];
    /** True when another GROUP in the same tree writes the same property
     *  (cross-container mode groups, e.g. `compression`). Set by the context
     *  after the tree is built. */
    propertyIsSharedWithSiblingGroup = false;

    restoreStateContexts?: RestoreContext[];
    lastContextId: string = "DEFAULT";
    cachedContexts = new Map<string, any>();
    initialState: any;
    activeHiddenOptionIds = new Set<string>();
    /**
     * The choice the tree selected when it was built (xconfig's default). When the selection is
     * lost or lands on a hidden choice, the group goes back HERE before trying the first visible
     * one: the first choice is not a default (Compress PDF lists its strongest level first).
     */
    defaultOptionId?: string;
    /** Buttons up to this many visible choices, a searchable dropdown above (xconfig; default 6). */
    maxButtonOptions?: number;

    get selected(): SelectableOptionViewState {
        return this.options.find(it => it.selected)
            ?? this.options.find(it => it.id === this.defaultOptionId)
            ?? this.options[0];
    }

    isHidden(results: ViewResult, shouldIgnore: (propertyName: string) => boolean = () => false) {
        const safeShouldIgnoreFn = () => {
            const alreadyEvaluatedPropertyNames = new Set<string>();
            return (_propertyName: string) => {
                if (alreadyEvaluatedPropertyNames.has(_propertyName)) {
                    // This is a request to reevaluate already evaluated property
                    return false;
                }
                alreadyEvaluatedPropertyNames.add(_propertyName);
                return shouldIgnore(_propertyName);
            }
        }
        const hidden = isHidden(this.hidden, results, safeShouldIgnoreFn());
        if (hidden) return true;
        // console.log(`${this.id} is hidden: ${hidden}`);

        // 1. Check if this group behaves as a hidden option within its own context (Self-Hiding)
        if (this.activeHiddenOptionIds.has(this.id)) {
            return true;
        }

        // 2. Check if this group is nested within another Group (Group -> Selectable -> Group)
        // and that ancestor group wants to hide this group.
        // Note: Direct parent is SelectableOptionViewState (for nested) or RootOptionViewState (for top-level).
        // So we check the grandparent.
        if (this.parent && this.parent.parent instanceof GroupViewState && this.parent.parent.activeHiddenOptionIds.has(this.id)) {
            return true;
        }

        return false;
    }

    updateValue(value: SelectableOptionViewState) {
        if (!(value instanceof SelectableOptionViewState)) {
            throw new Error("Method not implemented.");
        }
        this.options.forEach(it => it.selected = false);
        value.selected = true;
    }

    override getResultForView(context: ViewResult = {}): ViewResult {
        const result = {
            [this.propertyNames[0]]: this.selected.propertyValue
        };
        // Children see the surrounding context plus this group's own value so their
        // hidden clauses (e.g. keyed on the mode property) evaluate correctly.
        Object.assign(result, this.selected.getResultForView({ ...context, ...result }))

        return result;
    }

    override findByPropertyName(propertyName: string): ViewState | undefined {
        if (this.propertyNames?.includes(propertyName)) {
            return this;
        }
        for (const opt of this.options) {
            const found = opt.findByPropertyName(propertyName);
            if (found) return found;
        }
        return undefined;
    }

    override find(predicate: (id: string) => boolean): ViewState | undefined {
        if (predicate(this.id)) {
            return this;
        }
        for (const opt of this.options) {
            const found = opt.find(predicate);
            if (found) return found;
        }
        return undefined;
    }

    getSnapshot() {
        return {
            selectedValue: this.selected.propertyValue,
            options: this.options.map(o => o.getSnapshot())
        };
    }

    restoreSnapshot(snapshot: any) {
        if (!snapshot) return;

        // Restore selected option
        if (snapshot.selectedValue) {
            const optionToSelect = this.options.find(o => o.propertyValue === snapshot.selectedValue);
            if (optionToSelect) {
                this.updateValue(optionToSelect);
            }
        }

        // Restore children recursively
        if (Array.isArray(snapshot.options)) {
            this.options.forEach((o, i) => {
                if (snapshot.options[i]) {
                    o.restoreSnapshot(snapshot.options[i]);
                }
            });
        }
    }

    restoreDefaultValues(values: Record<string, any>) {
        if (!values) return;

        this.propertyNames.forEach(propName => {
            if (values[propName] !== undefined) {
                const valueToSelect = values[propName];
                const optionToSelect = this.options.find(o => o.propertyValue === valueToSelect);
                if (optionToSelect) {
                    this.updateValue(optionToSelect);
                }
            }
        });

        this.options.forEach(opt => {
            if (typeof opt.restoreDefaultValues === 'function') {
                opt.restoreDefaultValues(values);
            }
        });
    }

    /**
     * 
     * @param results ViewResult passed to remove items that are hidden on the UI
     * @returns 
     */
    ensureContextState(results: ViewResult) {
        // Context Restoration Logic
        if (this.restoreStateContexts) {
            let currentContextId = "DEFAULT";
            for (const ctx of this.restoreStateContexts) {
                // Check if condition is met. isHidden returns true if met.
                // ctx.condition can be Requirement[] or Requirement (if single object in JSON)
                const condition = Array.isArray(ctx.condition) ? ctx.condition : [ctx.condition];
                if (isHidden(condition, results)) {
                    currentContextId = ctx.id;
                    break;
                }
            }

            if (currentContextId !== this.lastContextId) {
                // Save current state (Deep Snapshot)
                this.cachedContexts.set(this.lastContextId, this.getSnapshot());

                // Determine value to restore
                let snapshotToRestore = this.initialState;
                let isDefaultState = true;
                if (this.cachedContexts.has(currentContextId)) {
                    snapshotToRestore = this.cachedContexts.get(currentContextId);
                    isDefaultState = false;
                }

                // Restore (Deep Snapshot)
                this.restoreSnapshot(snapshotToRestore);

                // If we didn't find a cached history, AND the context has defaultValues, apply them ON TOP of the initial state
                if (isDefaultState) {
                    const activeContext = this.restoreStateContexts.find(c => c.id === currentContextId);
                    if (activeContext && activeContext.defaultValues) {
                        this.restoreDefaultValues(activeContext.defaultValues);
                    }
                }

                this.lastContextId = currentContextId;

                // Update Hidden Options
                this.activeHiddenOptionIds.clear();
                const activeContext = this.restoreStateContexts.find(c => c.id === currentContextId);
                if (activeContext && activeContext.hiddenOptions) {
                    activeContext.hiddenOptions.forEach(id => this.activeHiddenOptionIds.add(id));
                }
            }
        }

        // Recurse to all children to ensure they update their context state
        this.options.forEach(opt => opt.ensureContextState(results));
    }

    override summarize(results: ViewResult): ViewResult {
        // Ensure context state is up to date for this group and ALL children (recursive)
        this.ensureContextState(results);

        const result = {};
        if (this.isHidden(results)) {
            // When ANOTHER group owns the same property (e.g. `compression` is written
            // by IMAGE_COMPRESSION on jpg/png pages and by GIF_FILE_COMPRESSION on gif
            // pages — exactly one of them visible per page), a hidden group must not
            // wipe the visible owner's value: whichever summarizes later would clobber
            // it, making every hide-condition keyed on that property evaluate against
            // undefined. Sole-owner groups keep the unset (clears stale state when a
            // context switch hides them).
            if (!this.propertyIsSharedWithSiblingGroup) {
                unset(results, this.propertyNames[0]);
            }
            return result;
        }

        // Check if the currently selected option is hidden based on the current results OR context
        if (this.selected.isHidden(results) || this.activeHiddenOptionIds.has(this.selected.id)) {
            // If hidden, go back to the group's default when that is visible, else the first visible option
            const isVisible = (opt: SelectableOptionViewState) => !opt.isHidden(results) && !this.activeHiddenOptionIds.has(opt.id);
            const defaultOption = this.options.find(opt => opt.id === this.defaultOptionId);
            const visibleOption = (defaultOption && isVisible(defaultOption)) ? defaultOption : this.options.find(isVisible);
            if (visibleOption) {
                this.updateValue(visibleOption);
            } else {
                // Every option in this group has an explicit hidden condition that evaluated to true for the current state - treat group as effectively hidden
                unset(results, this.propertyNames[0]);
                return result;
            }
        }

        result[this.propertyNames[0]] = this.selected.propertyValue;
        return Object.assign(result, this.selected.summarize(results));
    }

    hasAdvancedOptions(): boolean {
        if (this.optionLevel === OptionLevel.Advanced) {
            return true;
        }
        return this.options.some(it => it.hasAdvancedOptions());
    }
}

export class SelectableOptionViewState extends OptionViewState {
    id: any;
    name: string;
    selected: boolean;
    propertyValue: string;
    iconPath?: string;
    options: (SubgroupViewState | GroupViewState)[];
    notes?: Note[];
    hidden: Requirement[];
    optionLevel: OptionLevel = OptionLevel.Simple;
    /** xconfig's site-feature-flag marker; evaluated by `pdf-compress-presets.gate.ts`. */
    whenFeatureFlag?: { [flagName: string]: boolean };

    selectChild(model: OptionViewState) {
        throw new Error("Method not implemented.");
    }

    override findByPropertyName(propertyName: string): ViewState | undefined {
        for (const opt of this.options) {
            const found = opt.findByPropertyName(propertyName);
            if (found) return found;
        }
        return undefined;
    }

    override find(predicate: (id: string) => boolean): ViewState | undefined {
        if (predicate(this.id)) {
            return this;
        }
        for (const opt of this.options) {
            const found = opt.find(predicate);
            if (found) return found;
        }
        return undefined;
    }

    ensureContextState(results: ViewResult) {
        this.options.forEach(opt => opt.ensureContextState(results));
    }

    restoreDefaultValues(values: Record<string, any>) {
        // Pass through to children
        this.options.forEach(opt => {
            if (typeof (opt as any).restoreDefaultValues === 'function') {
                (opt as any).restoreDefaultValues(values);
            }
        });
    }

    getSnapshot() {
        return {
            options: this.options.map(o => o.getSnapshot())
        };
    }

    restoreSnapshot(snapshot: any) {
        if (snapshot && Array.isArray(snapshot.options)) {
            this.options.forEach((o, i) => {
                if (snapshot.options[i]) {
                    o.restoreSnapshot(snapshot.options[i]);
                }
            });
        }
    }

    isHidden(results: ViewResult, shouldIgnore: (propertyName: string) => boolean = () => false) {
        const safeShouldIgnoreFn = () => {
            const alreadyEvaluatedPropertyNames = new Set<string>();
            return (_propertyName: string) => {
                if (alreadyEvaluatedPropertyNames.has(_propertyName)) {
                    // This is a request to reevaluate already evaluated property
                    return false;
                }
                alreadyEvaluatedPropertyNames.add(_propertyName);
                return shouldIgnore(_propertyName);
            }
        }
        const hidden = isHidden(this.hidden, results, safeShouldIgnoreFn());
        if (hidden) return true;
        return this.parent && this.parent instanceof GroupViewState && this.parent.activeHiddenOptionIds.has(this.id);
    }

    override getResultForView(context: ViewResult = {}): ViewResult {
        const result = {};
        const groupAndSubGroupViewStates = this.options
            .filter(it => it instanceof GroupViewState || it instanceof SubgroupViewState)
        groupAndSubGroupViewStates.forEach(it => {
            // Same rule as RootOptionViewState.getResultForView and
            // getChildResultsForView: a hidden child that shares its property
            // with a sibling must not seed the view results — walk order would
            // otherwise let it clobber the visible owner's value (the percent
            // 100/500 twins under one mode land here, and the later hidden twin
            // was overwriting the visible one).
            if ((it as any).propertyIsSharedWithSiblingSubgroup
                && it instanceof SubgroupViewState
                && it.isHidden({ ...context, ...result })) {
                return;
            }
            if ((it as any).propertyIsSharedWithSiblingGroup
                && it instanceof GroupViewState
                && it.isHidden({ ...context, ...result })) {
                return;
            }
            Object.assign(result, it.getResultForView({ ...context, ...result }))
        });
        return result;
    }

    override summarize(results: ViewResult): ViewResult {
        const result = {};
        if (this.isHidden(results)) {
            return result;
        }
        const groupViewStates = this.options
            .filter(it => it instanceof GroupViewState || it instanceof SubgroupViewState)
        groupViewStates.forEach(it => {
            Object.assign(result, it.summarize(results))
        });
        return result;
    }

    hasAdvancedOptions(): boolean {
        if (this.optionLevel === OptionLevel.Advanced) {
            return true;
        }
        return this.options.some(it => (it instanceof GroupViewState || it instanceof SubgroupViewState) && it.hasAdvancedOptions());
    }
}

export class SubgroupViewState extends OptionViewState {
    name: string;
    type: string;
    propertyNames: string[];
    id: any;
    hidden: Requirement[];
    options: ValueViewState[];
    notes?: Note[];
    /** True when another SUBGROUP in the same tree writes the same property
     *  (per-context value variants with disjoint hidden clauses, e.g. the
     *  RANGE_100/RANGE_500 percent pair or the per-codec CRF subgroups).
     *  Set by the context after the tree is built — the SUBGROUP twin of
     *  GroupViewState.propertyIsSharedWithSiblingGroup. */
    propertyIsSharedWithSiblingSubgroup = false;

    getSnapshot() {
        return {
            options: this.options.map(o => o.getSnapshot())
        };
    }

    restoreSnapshot(snapshot: any) {
        if (snapshot && Array.isArray(snapshot.options)) {
            this.options.forEach((o, i) => {
                if (snapshot.options[i]) {
                    // option is a ValueViewState, allow it to restore its own snapshot
                    // ValueViewState snapshot is typically just the value.
                    // But wait, getSnapshot uses map(o.getSnapshot()), so snapshot is [val1, val2]
                    (o as any).restoreSnapshot(snapshot.options[i]);
                }
            });
        }
    }

    restoreDefaultValues(values: Record<string, any>) {
        this.propertyNames.forEach((prop, index) => {
            if (values[prop] !== undefined) {
                const child = this.options[index];
                if (child instanceof ValueViewState) {
                    (child as any).updateValue(values[prop]);
                }
            }
        });

        this.options.forEach(opt => {
            if (typeof (opt as any).restoreDefaultValues === 'function') {
                (opt as any).restoreDefaultValues(values);
            }
        });
    }

    isHidden(results: ViewResult, shouldIgnore: (propertyName: string) => boolean = () => false) {
        const safeShouldIgnoreFn = () => {
            const alreadyEvaluatedPropertyNames = new Set<string>();
            return (_propertyName: string) => {
                if (alreadyEvaluatedPropertyNames.has(_propertyName)) {
                    // This is a request to reevaluate already evaluated property
                    return false;
                }
                alreadyEvaluatedPropertyNames.add(_propertyName);
                return shouldIgnore(_propertyName);
            }
        }
        const hidden = isHidden(this.hidden, results, safeShouldIgnoreFn());
        if (hidden) return true;
        return this.parent && this.parent instanceof GroupViewState && this.parent.activeHiddenOptionIds.has(this.id);
    }

    override getResultForView(context: ViewResult = {}): ViewResult {
        const result = {};
        for (let index = 0; index < this.propertyNames.length; index++) {
            const propertyName = this.propertyNames[index];
            const option = this.options[index];
            result[propertyName] = option.getResultForView();
        }
        // Values revealed under a selected dropdown entry (nested subgroups) must
        // reach the view result too — for wire emission and for every hidden
        // clause keyed on them. Non-selected entries contribute nothing.
        for (const option of this.options) {
            Object.assign(result, option.getChildResultsForView({ ...context, ...result }));
        }
        return result;
    }

    override findByPropertyName(propertyName: string): ViewState | undefined {
        if (this.propertyNames?.includes(propertyName)) {
            return this;
        }
        for (const opt of this.options) {
            const found = opt.findByPropertyName(propertyName);
            if (found) return found;
        }
        return undefined;
    }

    override find(predicate: (id: string) => boolean): ViewState | undefined {
        if (predicate(this.id)) {
            return this;
        }
        for (const opt of this.options) {
            const found = opt.find(predicate);
            if (found) return found;
        }
        return undefined;
    }

    override summarize(results: ViewResult): ViewResult {
        const result = {};
        if (this.isHidden(results)) {
            // A hidden subgroup that SHARES its property with a sibling subgroup
            // (disjoint hidden clauses — exactly one visible at a time) must not
            // wipe the visible owner's value from the evaluation context: every
            // later hide-condition keyed on that property would then evaluate
            // against undefined. Sole owners keep the unset (clears stale state).
            // SUBGROUP twin of the GroupViewState.summarize shared-property rule.
            if (!this.propertyIsSharedWithSiblingSubgroup) {
                // Unset all properties associated with this subgroup
                this.propertyNames.forEach(name => unset(results, name));
            }
            return result;
        }

        for (let index = 0; index < this.propertyNames.length; index++) {
            const propertyName = this.propertyNames[index];
            const option = this.options[index];

            if (typeof (option as any).summarize === 'function') {
                const optionSummary = (option as any).summarize(results);

                if (optionSummary && typeof optionSummary === 'object') {
                    Object.assign(result, optionSummary);
                }

                result[propertyName] = (option as any).summarize(results);
            } else {
                result[propertyName] = option.getResultForView();
            }
        }
        // Nested reveals under the selected dropdown entry summarize like any
        // other visible child; non-selected entries contribute nothing.
        for (const option of this.options) {
            Object.assign(result, option.summarizeChildren(results));
        }
        return result;
    }
}

export class SelectableViewState {
    options: DropdownOptionViewState[];
    selected: DropdownOptionViewState;
}

export abstract class ValueViewState extends ViewState {
    uuid: string = StringUtils.getRandomString("a")
    id: string;
    type: string;

    abstract getResultForView(): string | number | boolean;

    // Abstract method for updating value, required for snapshot restoration
    // Actually not abstract in base because updateValue signature varies significantly (string, number, boolean)
    // But getSnapshot/restoreSnapshot can be generic if we cast or impl in subclasses.

    getSnapshot(): any {
        // Default implementation for simple value views:
        // We need 'this.value' ?? 
        // ValueViewState doesn't guarantee 'value' property exists on the base class (it's abstract)
        // So we must implement in subclasses or cast.
        // Let's implement in subclasses.
        return undefined;
    }

    restoreSnapshot(snapshot: any) {
        // Implement in subclasses
    }

    restoreDefaultValues(values: Record<string, any>) {
        // Default: Do nothing. Leaf nodes usually don't have children to restore.
        // But they might be the target of a value update!
        // The parent (Subgroup) handles updating MY value if I map to a propertyName.
        // So this method is mainly for recursion if I am a container.

        // HOWEVER: DropdownViewState MIGHT be a container?
    }

    /**
     * Values contributed by nested children revealed under this value node
     * (dropdown entries carrying subgroups reveal them on selection, mirroring
     * GROUP_SELECTION reveal at group level). Plain value leaves have none.
     */
    getChildResultsForView(context: ViewResult): ViewResult {
        return {};
    }

    /** Summaries of the nested children revealed under this value node. */
    summarizeChildren(results: ViewResult): ViewResult {
        return {};
    }
}

export class RangeViewState extends ValueViewState {
    max: number;
    min: number;
    step: number;
    value: number;

    updateValue(value: number) {
        this.value = value;
    }

    getResultForView() {
        return this.value;
    }

    getSnapshot() {
        return this.value;
    }

    restoreSnapshot(snapshot: any) {
        if (typeof snapshot === 'number') {
            this.updateValue(snapshot);
        }
    }
}

export class TextInputViewState extends ValueViewState {
    name: string;
    value: string;

    updateValue(value: string) {
        this.value = value;
    }

    getResultForView() {
        return this.value;
    }

    getSnapshot() {
        return this.value;
    }

    restoreSnapshot(snapshot: any) {
        if (typeof snapshot === 'string') {
            this.updateValue(snapshot);
        }
    }
}

export class BooleanViewState extends ValueViewState {
    name: string;
    value: boolean;
    updateValue(value: boolean) {
        this.value = value;
    }

    getResultForView() {
        return this.value;
    }

    getSnapshot() {
        return this.value;
    }

    restoreSnapshot(snapshot: any) {
        if (typeof snapshot === 'boolean') {
            this.updateValue(snapshot);
        }
    }
}

export class DropdownOptionViewState extends ValueViewState {
    name: string;
    value: string;
    selected: boolean;
    hidden?: Requirement[];
    /** Nested reveal children (GROUP_SELECTION entries can carry subgroups that
     *  show only while this entry is selected — same reveal-on-selection pattern
     *  every option group already uses). Undefined for plain entries. */
    options?: (SubgroupViewState | GroupViewState)[];

    isHidden(results: ViewResult) {
        return this.hidden && isHidden(this.hidden, results);
    }

    getResultForView() {
        // console.log('TODO FIXME')
        return this.value;
    }

    override getChildResultsForView(context: ViewResult): ViewResult {
        const result = {};
        (this.options ?? []).forEach(child => {
            // Same rule as RootOptionViewState.getResultForView: a definitely-hidden
            // child that shares its property with a sibling must not seed the view
            // results (walk order would let it clobber the visible owner's value).
            if (child instanceof SubgroupViewState
                && child.propertyIsSharedWithSiblingSubgroup
                && child.isHidden(context)) {
                return;
            }
            if (child instanceof GroupViewState
                && child.propertyIsSharedWithSiblingGroup
                && child.isHidden(context)) {
                return;
            }
            Object.assign(result, child.getResultForView({ ...context, ...result }));
        });
        return result;
    }

    override summarizeChildren(results: ViewResult): ViewResult {
        const result = {};
        (this.options ?? []).forEach(child => {
            Object.assign(result, child.summarize(results));
        });
        return result;
    }

    override restoreDefaultValues(values: Record<string, any>) {
        (this.options ?? []).forEach(child => {
            if (typeof (child as any).restoreDefaultValues === 'function') {
                (child as any).restoreDefaultValues(values);
            }
        });
    }

    override getSnapshot() {
        if (!this.options || this.options.length === 0) {
            return undefined;
        }
        return { options: this.options.map(o => o.getSnapshot()) };
    }

    override restoreSnapshot(snapshot: any) {
        if (snapshot && Array.isArray(snapshot.options) && this.options) {
            this.options.forEach((o, i) => {
                if (snapshot.options[i]) {
                    o.restoreSnapshot(snapshot.options[i]);
                }
            });
        }
    }
}

export class DropdownViewState extends ValueViewState {
    name: string;
    get value(): string {
        return this.selected.value;
    }
    options: DropdownOptionViewState[];

    get selected(): DropdownOptionViewState {
        return this.options.find(it => it.selected) ?? this.options[0];
    }

    set selected(value: DropdownOptionViewState) {
        const current = this.options.find(it => it.selected)
        if (current) {
            current.selected = false;
        }
        value.selected = true;
    }

    updateValue(dropdownValue: DropdownOptionViewState | string) {
        this.options.forEach(it => it.selected = false);
        if (typeof dropdownValue === 'string') {
            const option = this.options.find(it => it.value === dropdownValue);
            if (option) {
                option.selected = true;
            }
        } else {
            dropdownValue.selected = true;
        }
    }

    getResultForView() {
        return this.value;
    }

    /** Only the SELECTED entry's nested children contribute (mirrors how a
     *  GROUP only surfaces its selected GROUP_SELECTION's children). */
    override getChildResultsForView(context: ViewResult): ViewResult {
        return this.selected?.getChildResultsForView(context) ?? {};
    }

    override summarizeChildren(results: ViewResult): ViewResult {
        return this.selected?.summarizeChildren(results) ?? {};
    }

    private get hasEntryChildren(): boolean {
        return this.options.some(o => o.options && o.options.length > 0);
    }

    getSnapshot() {
        if (!this.hasEntryChildren) {
            return this.value;
        }
        // Entries with nested reveals also snapshot their children's values so a
        // context restore brings the revealed controls back, not just the selection.
        const entries: Record<string, any> = {};
        this.options.forEach(o => {
            const snap = o.getSnapshot();
            if (snap !== undefined) {
                entries[o.value] = snap;
            }
        });
        return { value: this.value, entries };
    }

    restoreSnapshot(snapshot: any) {
        const value = typeof snapshot === 'string' ? snapshot : snapshot?.value;
        if (typeof value === 'string') {
            // Find option with this value
            const option = this.options.find(o => o.value === value);
            if (option) {
                this.updateValue(option);
            }
        }
        if (snapshot && typeof snapshot === 'object' && snapshot.entries) {
            this.options.forEach(o => {
                if (snapshot.entries[o.value] !== undefined) {
                    o.restoreSnapshot(snapshot.entries[o.value]);
                }
            });
        }
    }

    /**
     * 
     * @param results ViewResult passed to remove items that are hidden on the UI
     * @returns 
     */
    ensureContextState(results: ViewResult) {
        // Only GroupViewState manages context restoration now.
    }

    restoreDefaultValues(values: Record<string, any>) {
        // The parent Subgroup sets this dropdown's own value (by propertyName index).
        // Entries carrying nested reveals forward the defaults to their children —
        // ALL entries, mirroring GroupViewState.restoreDefaultValues, so a restored
        // value lands even when its entry isn't currently selected.
        this.options.forEach(o => o.restoreDefaultValues(values));
    }

    /**
     * 
     * @param results ViewResult passed to remove items that are hidden on the UI
     * @returns 
     */
    summarize(results: ViewResult) {
        this.ensureContextState(results);

        const selected = this.selected;
        if (selected.isHidden(results)) {
            // If hidden, keep the value when a visible entry sends the same one (ICO pages swap
            // "32P" for "32 × 32", both 32P: a Re-edit restore of 32P must stay 32P), otherwise
            // fall back to the first visible option.
            const visibleOption = this.options.find(opt => opt.value === selected.value && !opt.isHidden(results))
                ?? this.options.find(opt => !opt.isHidden(results));
            if (visibleOption) {
                this.updateValue(visibleOption);
            }
        }
        return this.value;
    }
}

export class TextInputDropdownViewState extends ValueViewState {
    name: string;
    value: string;
    dropdown: DropdownViewState;

    /** No text in the field (`''`, or nothing at all). `0` / `"0"` is a value, never empty. */
    static isEmptyValue(value: unknown): boolean {
        return value === '' || value === undefined || value === null;
    }

    get isEmpty(): boolean {
        return TextInputDropdownViewState.isEmptyValue(this.value);
    }

    /**
     * `value::UNIT`, or `''` when the value is empty (R14Q-13, picker review 6 pass 2): an untouched
     * field used to read `"::SECONDS"`, a unit with nothing in front of it. `''` is the TREE's value
     * -- the view results, `summarize` and the restore snapshot (`initialSummary`) carry it, so a
     * reset still clears a typed value -- but it is NEVER posted: ironman cannot read `''` as unset
     * (`getBitrateV2` reads the unit after "::", `decodeTrimValue` throws "Unit  is not supported"),
     * so `ConvertOptionsContextV2.getSummary()` leaves an empty value::unit key OUT of the summary
     * every request is built from.
     */
    get combinedValue(): string {
        if (this.isEmpty) return '';
        return `${this.value}::${this.dropdown.value}`;
    }
    updateValue(value: string, dropdownValue?: DropdownOptionViewState) {
        if (!dropdownValue && typeof value === 'string' && value.includes('::')) {
            const parts = value.split('::');
            if (parts.length === 2) {
                this.value = parts[0];
                this.dropdown.updateValue(parts[1]);
                return;
            }
        }
        this.value = value;
        if (dropdownValue) {
            this.dropdown.updateValue(dropdownValue);
        }
    }

    getResultForView() {
        return this.combinedValue;
    }
}

export interface ReductionData {
    results: {
        resolutionPercentage?: string | number;
        toExtension?: string;
    };
    uploadInProgressFiles?: any;
}

export namespace Note {
    export const fn = {
        "APPROX_FILE_SIZE_REDUCTION_FN": APPROX_FILE_SIZE_REDUCTION_FN,
        "GIF_TARGET_SIZE_PER_FILE_FN": GIF_TARGET_SIZE_PER_FILE_FN,
        "GIF_MAX_FILE_SIZE_VERDICT_FN": GIF_MAX_FILE_SIZE_VERDICT_FN
    }

    function APPROX_FILE_SIZE_REDUCTION_FN(dataFill: ReductionData): string {
        const { resolutionPercentage, toExtension } = dataFill.results;

        // 1. Normalize values
        const value = Number(resolutionPercentage) || 100;
        const ratio = value / 100;
        const isGif = toExtension?.toUpperCase() === "GIF";

        // 2. Calculate Reductions (Inverse Square Law for dimensions vs area)
        // Area reduction is a good proxy for file size reduction in most codecs
        const sizeFactor = Math.pow(ratio, 2);
        const percentageReduction = (1 - sizeFactor) * 100;

        // 3. Determine Example Text
        // Priority: Actual file info > Generic 10MB example
        const actualFileInfo = dataFill.uploadInProgressFiles
            ? UPLOADED_FILE_TARGET_SIZE_BY_DIMENSION_FN(value, dataFill.uploadInProgressFiles)
            : '';

        const genericExample = `<p>For a <strong>10 MB</strong> file, this would result in an approximate size of <strong>${(10 * sizeFactor).toFixed(2)} MB</strong>.</p>`;
        // GIF output gets NO byte prediction: the per-file number is derived from the
        // SOURCE bytes, so on video->gif it predicts from the video (a 769 KB mp4 was
        // reading "will reduce to 491.90 KB" while its GIF output is ~1.8 MB), and even
        // gif->gif it would state a confident figure directly under the warning that
        // says dimension cuts may not shrink — or may grow — the file.
        const exampleContent = isGif ? '' : (actualFileInfo || genericExample);

        // 4. Build components for the template
        const gifWarning = `Shrinking a GIF's dimensions doesn't always shrink the file — it can even grow, due to how animation frames are optimized.`;
        const standardEstimate = `Estimated file size reduction: approximately <strong>${percentageReduction.toFixed(2)}%</strong>.`;

        return `
        <div style="font-family: sans-serif; line-height: 1.6;">
            <div><strong>Estimated impact</strong> — dimensions reduced to <strong>${value}%</strong> of the original.</div>

            <div style="margin-top: 8px;">
                ${isGif ? gifWarning : standardEstimate}
            </div>

            ${exampleContent}

            <div style="font-size: 0.9em; color: #777; margin-top: 10px;">
                <em>
                    <strong>Note:</strong> Actual file size depends on image complexity.
                    ${isGif ? "" : "Lower resolutions generally result in smaller files."}
                </em>
            </div>
        </div>`.trim();
    }
}